import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Receipt, Download, Building2, Trash2, Mail, Eye, DollarSign, AlertTriangle, FileCode2 } from "lucide-react";
import { format } from "date-fns";
import { formatLocalDateTime } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to parse date strings in various formats
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  
  // Check if DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, parseInt(month) - 1, day);
  }
  
  // Check if YYYY-MM-DD format or other standard formats
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  return null;
};

const Invoices = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [invoices, setInvoices] = useState([]);
  const [uninvoicedFlights, setUninvoicedFlights] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState("");
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [useInvoiceSmtp, setUseInvoiceSmtp] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportAgency, setExportAgency] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel");
  const [vatRateServices, setVatRateServices] = useState("0");
  const [vatRateParking, setVatRateParking] = useState("0");
  const [vatRateCosts, setVatRateCosts] = useState("0");
  const [manualItems, setManualItems] = useState([]);
  const [addFeesDialogOpen, setAddFeesDialogOpen] = useState(false);
  const [feesFormData, setFeesFormData] = useState({
    legal_interest_percentage: 0,
    legal_interest_minimum_amount: 0,
    legal_interest_remark: "",
    administration_fee: 0,
    administration_fee_remark: ""
  });
  const [overdueInfo, setOverdueInfo] = useState(null);

  useEffect(() => {
    if (!selectedAgency) {
      setOverdueInfo(null);
      return;
    }
    const fetchOverdue = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/agencies/${selectedAgency}/overdue-invoices?days=60`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOverdueInfo(res.data);
      } catch (err) {
        setOverdueInfo(null);
      }
    };
    fetchOverdue();
  }, [selectedAgency]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [invoicesRes, flightsRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/flights?invoiced=false`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invoicesRes.data);
      setUninvoicedFlights(flightsRes.data);
      setAgencies(agenciesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
    setLoading(false);
  };

  const addManualItem = () => {
    setManualItems([...manualItems, {
      description: "",
      unit_price: "",
      quantity: "1",
      category: "costs", // "costs" or "handling"
      vat_rate: "0" // Per-item VAT: 0%, 6%, 12%, 21%
    }]);
  };

  const removeManualItem = (index) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  const updateManualItem = (index, field, value) => {
    const updated = [...manualItems];
    updated[index][field] = value;
    setManualItems(updated);
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    if (selectedFlights.length === 0 && manualItems.length === 0) {
      toast.error("Please select at least one flight or add manual items");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices`, {
        agency_id: selectedAgency,
        flight_ids: selectedFlights,
        vat_rate_services: parseFloat(vatRateServices),
        vat_rate_parking: parseFloat(vatRateParking),
        vat_rate_costs: parseFloat(vatRateCosts),
        manual_items: manualItems.filter(item => item.description && item.unit_price).map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          quantity: parseInt(item.quantity) || 1,
          vat_rate: parseFloat(item.vat_rate) || 0
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice generated successfully");
      setOpen(false);
      setSelectedAgency("");
      setSelectedFlights([]);
      setVatRateServices("0");
      setVatRateParking("0");
      setVatRateCosts("0");
      setManualItems([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const invoice = invoices.find(inv => inv.id === invoiceId);
      link.setAttribute('download', `invoice_${invoice?.invoice_number || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadPeppolXML = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/peppol-xml`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const invoice = invoices.find(inv => inv.id === invoiceId);
      const safeNumber = (invoice?.invoice_number || invoiceId).toString().replace(/[^A-Za-z0-9._-]/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `peppol_${safeNumber}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Peppol XML downloaded — upload it to WinAuditor");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'string') {
        toast.error(detail);
      } else {
        // Blob error response: read it
        try {
          const text = await error.response?.data?.text?.();
          const parsed = text ? JSON.parse(text) : null;
          toast.error(parsed?.detail || "Failed to generate Peppol XML");
        } catch {
          toast.error("Failed to generate Peppol XML");
        }
      }
    }
  };

  const handleDownloadWinAuditorXML = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/winauditor-xml`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const invoice = invoices.find(inv => inv.id === invoiceId);
      const safeNumber = (invoice?.invoice_number || invoiceId).toString().replace(/[^A-Za-z0-9._-]/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `winauditor_${safeNumber}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("WinAuditor XML downloaded — import it into WinAuditor (Sales), then send via Peppol");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'string') {
        toast.error(detail);
      } else {
        try {
          const text = await error.response?.data?.text?.();
          const parsed = text ? JSON.parse(text) : null;
          toast.error(parsed?.detail || "Failed to generate WinAuditor XML");
        } catch {
          toast.error("Failed to generate WinAuditor XML");
        }
      }
    }
  };

  const handleOpenEmailDialog = (invoice) => {
    setSelectedInvoice(invoice);
    // Find agency and pre-fill email
    const agency = agencies.find(a => a.id === invoice.agency_id);
    setRecipientEmail(agency?.email || "");
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter recipient email");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices/${selectedInvoice.id}/send-email?recipient_email=${encodeURIComponent(recipientEmail)}&use_invoice_smtp=${useInvoiceSmtp}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice email sent successfully");
      setEmailDialogOpen(false);
      setUseInvoiceSmtp(false); // Reset selection
      fetchData(); // Refresh to get updated email history
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const agencyParam = exportAgency !== "all" ? `&agency_id=${exportAgency}` : "";
      const url = `${API}/invoices/export?format=${exportFormat}${agencyParam}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const filename = exportFormat === 'excel' 
        ? `agency_statements_${new Date().toISOString().split('T')[0]}.xlsx`
        : `agency_statements_${new Date().toISOString().split('T')[0]}.docx`;
      
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      toast.success(`Exported successfully as ${exportFormat === 'excel' ? 'Excel' : 'Word'} document`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export");
    }
  };

  const handleViewHistory = (invoice) => {
    setSelectedInvoice(invoice);
    setHistoryDialogOpen(true);
  };

  const handleUpdateStatus = async (invoiceId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber, invoiceStatus) => {
    // Different message for draft vs non-draft
    const isDraft = invoiceStatus === 'draft';
    const message = isDraft 
      ? `Completely DELETE draft invoice ${invoiceNumber}? This cannot be undone.`
      : `Delete invoice ${invoiceNumber}? This will create a credit note and mark flights as uninvoiced.`;
    
    if (!window.confirm(message)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show appropriate success message
      if (response.data.deleted) {
        toast.success(`Draft invoice ${invoiceNumber} completely deleted`);
      } else {
        toast.success(`Invoice deleted. Credit note ${response.data.credit_note_number} created.`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleOpenAddFeesDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setFeesFormData({
      legal_interest_percentage: 0,
      legal_interest_minimum_amount: 0,
      legal_interest_remark: "",
      administration_fee: 0,
      administration_fee_remark: ""
    });
    setAddFeesDialogOpen(true);
  };

  const handleApplyFees = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/invoices/${selectedInvoice.id}/add-fees`,
        feesFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Fees added successfully. New invoice ${selectedInvoice.invoice_number}/2 created.`);
      setAddFeesDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add fees");
    }
  };

  const toggleFlightSelection = (flightId) => {
    setSelectedFlights(prev => 
      prev.includes(flightId) 
        ? prev.filter(id => id !== flightId)
        : [...prev, flightId]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#4ADE80';
      case 'sent': return '#60A5FA';
      case 'approved': return '#FBBF24';
      default: return '#B0B0B0';
    }
  };

  // Filter and sort flights by date (newest first for invoicing)
  const agencyFlights = selectedAgency 
    ? uninvoicedFlights
        .filter(f => f.agency_id === selectedAgency)
        .sort((a, b) => {
          const dateA = parseDateString(a.flight_date);
          const dateB = parseDateString(b.flight_date);
          if (!dateA || !dateB) return 0;
          return dateB - dateA; // Newest first
        })
    : [];

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading...</div>;

  return (
    <div data-testid="invoices-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Invoicing</h1>
          <p style={{ color: '#B0B0B0' }}>Generate and manage invoices</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setExportDialogOpen(true)} style={{ background: '#10B981', color: '#FFFFFF' }}>
            <Download className="mr-2 h-4 w-4" />Export Statements
          </Button>
          {(currentUser.role === 'admin' || currentUser.role === 'accounting') && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button style={{ background: '#FFFFFF', color: '#0A1128' }} data-testid="generate-invoice-button">
                  <Plus className="mr-2 h-4 w-4" />Generate Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFFFFF' }}>Generate Invoice</DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>Select agency and flights to invoice</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleGenerateInvoice} className="space-y-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency *</Label>
                <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    {agencies.filter(a => a.is_active !== false).sort((a, b) => a.name.localeCompare(b.name)).map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {overdueInfo && overdueInfo.count > 0 && (
                <div
                  className="rounded-md p-3 flex gap-3"
                  data-testid="overdue-invoices-warning"
                  style={{
                    background: 'rgba(245, 158, 11, 0.12)',
                    border: '1px solid #F59E0B',
                  }}
                >
                  <AlertTriangle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }} />
                  <div className="flex-1 text-sm" style={{ color: '#FDE68A' }}>
                    <div className="font-semibold mb-1" style={{ color: '#F59E0B' }}>
                      Overdue Payments Notice
                    </div>
                    <div className="mb-2">
                      This agency has <strong>{overdueInfo.count}</strong> unpaid invoice
                      {overdueInfo.count > 1 ? 's' : ''} older than {overdueInfo.threshold_days} days
                      &nbsp;— total <strong>€{overdueInfo.total_overdue_amount.toFixed(2)}</strong>
                      &nbsp;(oldest: {overdueInfo.oldest_days} days).
                    </div>
                    <details className="cursor-pointer">
                      <summary className="text-xs" style={{ color: '#FDE68A' }}>
                        View overdue invoices
                      </summary>
                      <ul className="mt-2 ml-2 space-y-1 text-xs" style={{ color: '#E8E8E8' }}>
                        {overdueInfo.invoices.slice(0, 10).map((inv) => (
                          <li key={inv.invoice_number}>
                            <span className="font-mono">{inv.invoice_number}</span>
                            &nbsp;— {inv.invoice_date || 'N/A'}
                            &nbsp;— €{inv.total_amount.toFixed(2)}
                            &nbsp;— <span style={{ color: '#F59E0B' }}>{inv.days_overdue}d overdue</span>
                          </li>
                        ))}
                        {overdueInfo.invoices.length > 10 && (
                          <li style={{ color: '#B0B0B0' }}>
                            ...and {overdueInfo.invoices.length - 10} more
                          </li>
                        )}
                      </ul>
                    </details>
                  </div>
                </div>
              )}
              
              {selectedAgency && (
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Select Flights</Label>
                  <div className="space-y-2 mt-2 max-h-96 overflow-y-auto p-3 rounded" style={{ background: '#0A1128', border: '1px solid #4A5A8A' }}>
                    {agencyFlights.length === 0 ? (
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>No uninvoiced flights for this agency</p>
                    ) : (
                      agencyFlights.map((flight) => (
                        <div key={flight.id} className="flex items-center space-x-2 p-2 rounded hover:bg-opacity-50" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                          <Checkbox
                            id={flight.id}
                            checked={selectedFlights.includes(flight.id)}
                            onCheckedChange={() => toggleFlightSelection(flight.id)}
                          />
                          <label htmlFor={flight.id} className="flex-1 cursor-pointer">
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-medium" style={{ color: '#E8E8E8' }}>{flight.flight_number}</span>
                                <span className="text-sm ml-2" style={{ color: '#B0B0B0' }}>
                                  {flight.flight_date}
                                </span>
                              </div>
                              <span className="font-medium" style={{ color: '#FFFFFF' }}>
                                €{flight.total_amount?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {selectedFlights.length > 0 && (
                    <div className="mt-2 p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#E8E8E8' }}>Total Amount:</span>
                        <span className="text-xl font-bold" style={{ color: '#FFFFFF' }}>
                          €{agencyFlights
                            .filter(f => selectedFlights.includes(f.id))
                            .reduce((sum, f) => sum + (f.total_amount || 0), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* VAT Selection */}
              {selectedFlights.length > 0 && (
                <div className="space-y-4 mt-4 p-4 rounded" style={{ border: '1px solid #4A5A8A', background: 'rgba(74, 90, 138, 0.1)' }}>
                  <h4 style={{ color: '#FFD700', fontSize: '14px', fontWeight: 'bold' }}>VAT Rates</h4>
                  
                  <div>
                    <Label htmlFor="vatServices" style={{ color: '#E8E8E8', fontSize: '12px' }}>VAT for Basic Services</Label>
                    <select
                      id="vatServices"
                      value={vatRateServices}
                      onChange={(e) => setVatRateServices(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '4px',
                        background: '#0A1128',
                        border: '1px solid #4A5A8A',
                        borderRadius: '4px',
                        color: '#E8E8E8'
                      }}
                    >
                      <option value="0">Exemption (0%)</option>
                      <option value="6">6%</option>
                      <option value="12">12%</option>
                      <option value="21">21%</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="vatParking" style={{ color: '#E8E8E8', fontSize: '12px' }}>VAT for Parking Fees</Label>
                    <select
                      id="vatParking"
                      value={vatRateParking}
                      onChange={(e) => setVatRateParking(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '4px',
                        background: '#0A1128',
                        border: '1px solid #4A5A8A',
                        borderRadius: '4px',
                        color: '#E8E8E8'
                      }}
                    >
                      <option value="0">Exemption (0%)</option>
                      <option value="6">6%</option>
                      <option value="12">12%</option>
                      <option value="21">21%</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="vatCosts" style={{ color: '#E8E8E8', fontSize: '12px' }}>VAT for Other Costs</Label>
                    <select
                      id="vatCosts"
                      value={vatRateCosts}
                      onChange={(e) => setVatRateCosts(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '4px',
                        background: '#0A1128',
                        border: '1px solid #4A5A8A',
                        borderRadius: '4px',
                        color: '#E8E8E8'
                      }}
                    >
                      <option value="0">Exemption (0%)</option>
                      <option value="6">6%</option>
                      <option value="12">12%</option>
                      <option value="21">21%</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Manual Items Section */}
              {selectedAgency && (
                <div className="space-y-4 mt-4 p-4 rounded" style={{ border: '1px solid #4A5A8A', background: 'rgba(74, 90, 138, 0.1)' }}>
                  <div className="flex justify-between items-center">
                    <h4 style={{ color: '#FFD700', fontSize: '14px', fontWeight: 'bold' }}>Manual Items (Optional)</h4>
                    <Button
                      type="button"
                      onClick={addManualItem}
                      size="sm"
                      style={{ background: '#22C55E', color: '#FFFFFF' }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  {manualItems.length > 0 && (
                    <div className="space-y-3">
                      {manualItems.map((item, index) => (
                        <div key={index} className="p-3 rounded space-y-2" style={{ background: '#0A1128', border: '1px solid #4A5A8A' }}>
                          <div className="flex justify-between items-start">
                            <span style={{ color: '#E8E8E8', fontSize: '12px', fontWeight: '500' }}>Item {index + 1}</span>
                            <Button
                              type="button"
                              onClick={() => removeManualItem(index)}
                              size="sm"
                              variant="ghost"
                              style={{ color: '#EF4444', padding: '2px 6px' }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <div>
                            <Label style={{ color: '#E8E8E8', fontSize: '11px' }}>Description *</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateManualItem(index, 'description', e.target.value)}
                              placeholder="Item description"
                              style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8', marginTop: '4px' }}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label style={{ color: '#E8E8E8', fontSize: '11px' }}>Unit Price (€) *</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateManualItem(index, 'unit_price', e.target.value)}
                                placeholder="0.00"
                                style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8', marginTop: '4px' }}
                              />
                            </div>
                            <div>
                              <Label style={{ color: '#E8E8E8', fontSize: '11px' }}>Quantity</Label>
                              <Input
                                type="number"
                                step="1"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateManualItem(index, 'quantity', e.target.value)}
                                style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8', marginTop: '4px' }}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label style={{ color: '#E8E8E8', fontSize: '11px' }}>Category</Label>
                            <select
                              value={item.category}
                              onChange={(e) => updateManualItem(index, 'category', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                marginTop: '4px',
                                background: '#1E2A4A',
                                border: '1px solid #4A5A8A',
                                borderRadius: '4px',
                                color: '#E8E8E8',
                                fontSize: '13px'
                              }}
                            >
                              <option value="costs">Cost</option>
                              <option value="handling">Handling</option>
                            </select>
                          </div>
                          
                          <div>
                            <Label style={{ color: '#E8E8E8', fontSize: '11px' }}>VAT Rate</Label>
                            <select
                              data-testid={`manual-item-vat-${index}`}
                              value={item.vat_rate}
                              onChange={(e) => updateManualItem(index, 'vat_rate', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                marginTop: '4px',
                                background: '#1E2A4A',
                                border: '1px solid #4A5A8A',
                                borderRadius: '4px',
                                color: '#E8E8E8',
                                fontSize: '13px'
                              }}
                            >
                              <option value="0">Exemption (0%)</option>
                              <option value="6">6%</option>
                              <option value="12">12%</option>
                              <option value="21">21%</option>
                            </select>
                          </div>
                          
                          {item.unit_price && item.quantity && (
                            <div className="flex justify-between items-center pt-2" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
                              <span style={{ color: '#B0B0B0', fontSize: '11px' }}>Net:</span>
                              <span style={{ color: '#E8E8E8', fontSize: '12px' }}>
                                €{(parseFloat(item.unit_price) * parseInt(item.quantity || 1)).toFixed(2)}
                              </span>
                              {parseFloat(item.vat_rate) > 0 && (
                                <>
                                  <span style={{ color: '#B0B0B0', fontSize: '11px' }}>VAT ({item.vat_rate}%):</span>
                                  <span style={{ color: '#FFD700', fontSize: '12px' }}>
                                    €{(parseFloat(item.unit_price) * parseInt(item.quantity || 1) * parseFloat(item.vat_rate) / 100).toFixed(2)}
                                  </span>
                                </>
                              )}
                              <span style={{ color: '#B0B0B0', fontSize: '11px' }}>Total:</span>
                              <span style={{ color: '#22C55E', fontSize: '13px', fontWeight: '600' }}>
                                €{((parseFloat(item.unit_price) * parseInt(item.quantity || 1)) * (1 + parseFloat(item.vat_rate) / 100)).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      <div className="flex justify-between items-center p-2 rounded" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                        <span style={{ color: '#E8E8E8', fontSize: '12px', fontWeight: '500' }}>Manual Items Total (incl. VAT):</span>
                        <span style={{ color: '#22C55E', fontSize: '14px', fontWeight: 'bold' }}>
                          €{manualItems.reduce((sum, item) => {
                            const price = parseFloat(item.unit_price) || 0;
                            const qty = parseInt(item.quantity) || 1;
                            const vatRate = parseFloat(item.vat_rate) || 0;
                            const net = price * qty;
                            return sum + net + (net * vatRate / 100);
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={selectedFlights.length === 0 && manualItems.length === 0}
                style={{ background: '#FFFFFF', color: '#0A1128' }}
              >
                Generate Invoice
              </Button>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {invoices.map((invoice) => (
          <Card key={invoice.id} className="card-hover" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Receipt className="h-6 w-6" style={{ color: '#FFFFFF' }} />
                  <div>
                    <div style={{ color: '#E8E8E8' }} className="text-xl">{invoice.invoice_number}</div>
                    <div className="text-sm font-normal" style={{ color: '#B0B0B0' }}>
                      {invoice.invoice_date || 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(currentUser.role === 'admin' || currentUser.role === 'accounting') && (
                    <>
                      <Select value={invoice.status} onValueChange={(status) => handleUpdateStatus(invoice.id, status)}>
                        <SelectTrigger className="w-32" style={{ borderColor: '#FFFFFF', color: getStatusColor(invoice.status) }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(invoice.id)} style={{ borderColor: '#FFFFFF', color: '#FFFFFF' }}>
                    <Download className="h-4 w-4 mr-2" />PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadWinAuditorXML(invoice.id)}
                    style={{ borderColor: '#34D399', color: '#34D399' }}
                    data-testid={`download-winauditor-xml-${invoice.id}`}
                    title="Download WinAuditor sales-import XML — import into WinAuditor, then send via Peppol"
                  >
                    <FileCode2 className="h-4 w-4 mr-2" />WinAuditor
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPeppolXML(invoice.id)}
                    style={{ borderColor: '#FACC15', color: '#FACC15' }}
                    data-testid={`download-peppol-xml-${invoice.id}`}
                    title="Download Peppol BIS 3.0 UBL XML (generic e-invoice format)"
                  >
                    <FileCode2 className="h-4 w-4 mr-2" />Peppol XML
                  </Button>
                  {currentUser.role !== 'agency' && (
                    <Button variant="outline" size="sm" onClick={() => handleOpenEmailDialog(invoice)} style={{ borderColor: '#60A5FA', color: '#60A5FA' }}>
                      <Mail className="h-4 w-4 mr-2" />Send
                    </Button>
                  )}
                  {currentUser.role === 'admin' && !invoice.invoice_number?.includes('/2') && (
                    <Button variant="outline" size="sm" onClick={() => handleOpenAddFeesDialog(invoice)} style={{ borderColor: '#FFA500', color: '#FFA500' }}>
                      <DollarSign className="h-4 w-4 mr-2" />Add Fees
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleViewHistory(invoice)} style={{ borderColor: '#A0A0A0', color: '#A0A0A0' }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  {(currentUser.role === 'admin' || currentUser.role === 'accounting') && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number, invoice.status)} style={{ color: '#FF6B6B' }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" style={{ color: '#FFFFFF' }} />
                  <div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Agency</div>
                    <div className="font-medium" style={{ color: '#E8E8E8' }}>{invoice.agency_name}</div>
                  </div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Flights</div>
                  <div className="font-medium" style={{ color: '#E8E8E8' }}>{invoice.flight_ids?.length || 0}</div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Total Amount</div>
                  <div className="font-medium text-lg" style={{ color: '#FFFFFF' }}>€{(invoice.total_amount_with_vat ?? invoice.total_amount)?.toFixed(2) || '0.00'}</div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Due Date</div>
                  <div className="font-medium" style={{ color: '#E8E8E8' }}>
                    {invoice.due_date ? (() => {
                      const date = parseDateString(invoice.due_date);
                      return date ? format(date, 'dd/MM/yyyy') : 'N/A';
                    })() : 'N/A'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {invoices.length === 0 && (
        <div className="text-center py-12">
          <Receipt className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
          <p style={{ color: '#B0B0B0' }}>No invoices found. Generate your first invoice.</p>
        </div>
      )}

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Send Invoice by Email</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Send invoice {selectedInvoice?.invoice_number} to agency
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Recipient Email *</Label>
              <Input 
                type="email" 
                value={recipientEmail} 
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="agency@example.com"
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Email Server</Label>
              <Select value={useInvoiceSmtp ? "invoice" : "default"} onValueChange={(val) => setUseInvoiceSmtp(val === "invoice")}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="default" style={{ color: '#E8E8E8' }}>Default Email Server</SelectItem>
                  <SelectItem value="invoice" style={{ color: '#E8E8E8' }}>Invoice Email Server</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                Choose which email server to use for sending this invoice
              </p>
            </div>
            <div className="text-sm" style={{ color: '#B0B0B0' }}>
              The invoice PDF will be attached to the email automatically.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} style={{ background: '#3B82F6', color: '#FFFFFF' }}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Email History</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Invoice {selectedInvoice?.invoice_number} - Email send history
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {selectedInvoice?.email_history && selectedInvoice.email_history.length > 0 ? (
              selectedInvoice.email_history.map((item, index) => (
                <Card key={index} style={{ borderColor: '#4A5A8A', background: '#0A1128' }}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Sent To</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>{item.sent_to}</p>
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Sent By</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>
                          {item.sent_by_name} ({item.sent_by})
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Sent At</p>
                        <p className="font-medium" style={{ color: '#E8E8E8' }}>
                          {formatLocalDateTime(item.sent_at)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
                <p style={{ color: '#B0B0B0' }}>No emails sent yet for this invoice</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Export Agency Statements</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Export invoices with running balance to Excel or Word document
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="excel" style={{ color: '#E8E8E8' }}>Excel (.xlsx)</SelectItem>
                  <SelectItem value="word" style={{ color: '#E8E8E8' }}>Word Document (.docx)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ color: '#E8E8E8' }}>Filter by Agency</Label>
              <Select value={exportAgency} onValueChange={setExportAgency}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all" style={{ color: '#E8E8E8' }}>All Agencies</SelectItem>
                  {agencies.filter(a => a.is_active !== false).sort((a, b) => a.name.localeCompare(b.name)).map((agency) => (
                    <SelectItem key={agency.id} value={agency.id} style={{ color: '#E8E8E8' }}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm" style={{ color: '#B0B0B0' }}>
              <p><strong>Export includes:</strong></p>
              <ul className="list-disc list-inside mt-2">
                <li>Invoice Number, Date, Agency</li>
                <li>Total Amount</li>
                <li>Payment Status</li>
                <li>Running Balance (cumulative total)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
              Cancel
            </Button>
            <Button onClick={handleExport} style={{ background: '#10B981', color: '#FFFFFF' }}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fees Dialog */}
      <Dialog open={addFeesDialogOpen} onOpenChange={setAddFeesDialogOpen}>
        <DialogContent style={{ background: '#0F1E38', borderColor: '#4A5A8A', maxWidth: '600px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Add Late Payment Fees</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Add legal interest and administration fees for invoice {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Line 1: Legal Interest Percentage */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.1) 0%, rgba(255, 140, 0, 0.05) 100%)',
              border: '1px solid rgba(255, 165, 0, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#FFA500', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                1. Legal Interest Fee (Percentage)
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Interest Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feesFormData.legal_interest_percentage}
                    onChange={(e) => setFeesFormData({ ...feesFormData, legal_interest_percentage: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g., 5"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    disabled={feesFormData.legal_interest_minimum_amount > 0}
                  />
                  {selectedInvoice && feesFormData.legal_interest_percentage > 0 && (
                    <p style={{ color: '#4ADE80', fontSize: '12px', marginTop: '4px' }}>
                      Amount: €{((selectedInvoice.total_amount * feesFormData.legal_interest_percentage) / 100).toFixed(2)}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Remark (Optional)</Label>
                  <Input
                    type="text"
                    value={feesFormData.legal_interest_remark}
                    onChange={(e) => setFeesFormData({ ...feesFormData, legal_interest_remark: e.target.value })}
                    placeholder="e.g., Legal interest as per contract terms"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    disabled={feesFormData.legal_interest_minimum_amount > 0}
                  />
                </div>
              </div>
            </div>

            {/* Line 2: Legal Interest Minimum Amount */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.1) 0%, rgba(255, 140, 0, 0.05) 100%)',
              border: '1px solid rgba(255, 165, 0, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#FFA500', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                2. Legal Interest Fee (Fixed Minimum Amount)
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Minimum Amount (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feesFormData.legal_interest_minimum_amount}
                    onChange={(e) => setFeesFormData({ ...feesFormData, legal_interest_minimum_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g., 50"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    disabled={feesFormData.legal_interest_percentage > 0}
                  />
                </div>
                
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Remark (Optional)</Label>
                  <Input
                    type="text"
                    value={feesFormData.legal_interest_remark}
                    onChange={(e) => setFeesFormData({ ...feesFormData, legal_interest_remark: e.target.value })}
                    placeholder="e.g., Minimum legal interest fee"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    disabled={feesFormData.legal_interest_percentage > 0}
                  />
                </div>
              </div>
            </div>

            {/* Line 3: Administration Fee */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#60A5FA', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                3. Administration Fee
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Fee Amount (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feesFormData.administration_fee}
                    onChange={(e) => setFeesFormData({ ...feesFormData, administration_fee: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g., 50"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
                
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Remark (Optional)</Label>
                  <Input
                    type="text"
                    value={feesFormData.administration_fee_remark}
                    onChange={(e) => setFeesFormData({ ...feesFormData, administration_fee_remark: e.target.value })}
                    placeholder="e.g., Administrative costs for late payment processing"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            {selectedInvoice && (
              <div style={{
                background: 'rgba(74, 222, 128, 0.1)',
                border: '1px solid rgba(74, 222, 128, 0.3)',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <p style={{ color: '#4ADE80', fontSize: '14px', fontWeight: '600' }}>New Invoice Summary:</p>
                <div style={{ color: '#E8E8E8', fontSize: '13px', marginTop: '8px' }}>
                  <div className="flex justify-between">
                    <span>Original Total:</span>
                    <span>€{selectedInvoice.total_amount.toFixed(2)}</span>
                  </div>
                  {feesFormData.legal_interest_percentage > 0 && (
                    <div className="flex justify-between" style={{ color: '#FFA500' }}>
                      <span>+ Legal Interest ({feesFormData.legal_interest_percentage}%):</span>
                      <span>€{((selectedInvoice.total_amount * feesFormData.legal_interest_percentage) / 100).toFixed(2)}</span>
                    </div>
                  )}
                  {feesFormData.legal_interest_minimum_amount > 0 && (
                    <div className="flex justify-between" style={{ color: '#FFA500' }}>
                      <span>+ Legal Interest (Min. Amount):</span>
                      <span>€{feesFormData.legal_interest_minimum_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {feesFormData.administration_fee > 0 && (
                    <div className="flex justify-between" style={{ color: '#60A5FA' }}>
                      <span>+ Administration Fee:</span>
                      <span>€{feesFormData.administration_fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold mt-2 pt-2" style={{ borderTop: '1px solid rgba(74, 222, 128, 0.3)', color: '#4ADE80' }}>
                    <span>New Total:</span>
                    <span>€{(
                      selectedInvoice.total_amount +
                      (feesFormData.legal_interest_percentage > 0 ? (selectedInvoice.total_amount * feesFormData.legal_interest_percentage) / 100 : 0) +
                      (feesFormData.legal_interest_minimum_amount || 0) +
                      (feesFormData.administration_fee || 0)
                    ).toFixed(2)}</span>
                  </div>
                  <p style={{ color: '#B0B0B0', fontSize: '12px', marginTop: '8px' }}>
                    New Invoice Number: {selectedInvoice.invoice_number}/2
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFeesDialogOpen(false)} style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplyFees} 
              style={{ background: '#FFA500', color: '#FFFFFF' }}
              disabled={feesFormData.legal_interest_percentage === 0 && feesFormData.legal_interest_minimum_amount === 0 && feesFormData.administration_fee === 0}
            >
              Apply Fees
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
